/**
 * <promo-bar>
 *
 * Rotates between the messages inside .promo-bar__track. The outgoing message
 * slides up and out of the bar while the incoming one slides up into place from
 * below. Pauses on hover, on keyboard focus, and while scrolled out of view.
 *
 * Deliberately named promo-bar, NOT announcement-bar: main.js:3777 defines
 * announcement-bar without a customElements.get() guard, so reusing that name
 * would throw and take the page down.
 */
if (!customElements.get('promo-bar')) {
  customElements.define(
    'promo-bar',
    class PromoBar extends HTMLElement {
      connectedCallback() {
        this.messages = Array.from(this.querySelectorAll('.promo-bar__message'));
        if (this.messages.length < 2) return;

        this.currentIndex = 0;
        this.isPaused = false;
        this.interval = null;
        this.resetTimer = null;

        // Duration comes from the section's own CSS so the two never drift apart.
        // Reduced motion is handled entirely in CSS (transform neutralised, the
        // opacity/visibility timings kept), so there is no branch for it here.
        const declared = getComputedStyle(this).getPropertyValue('--promo-bar-duration');
        this.duration = parseInt(declared, 10) || 500;

        this.delay = parseInt(this.dataset.interval, 10);
        if (!this.delay || this.delay < 1000) this.delay = 5000;

        this.handlePause = this.pause.bind(this);
        this.handleResume = this.resume.bind(this);

        this.addEventListener('mouseenter', this.handlePause);
        this.addEventListener('mouseleave', this.handleResume);
        this.addEventListener('focusin', this.handlePause);
        this.addEventListener('focusout', this.handleResume);

        // Mirrors the stock announcement bar: no work while off screen.
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.target !== this) return;
              if (entry.isIntersecting) {
                this.resume();
              } else {
                this.pause();
              }
            });
          });
          this.observer.observe(this);
        } else {
          this.start();
        }
      }

      disconnectedCallback() {
        this.stop();
        if (this.resetTimer) clearTimeout(this.resetTimer);
        if (this.observer) this.observer.disconnect();
        this.removeEventListener('mouseenter', this.handlePause);
        this.removeEventListener('mouseleave', this.handleResume);
        this.removeEventListener('focusin', this.handlePause);
        this.removeEventListener('focusout', this.handleResume);
      }

      start() {
        this.stop();
        if (this.isPaused || !this.messages || this.messages.length < 2) return;
        this.interval = setInterval(this.advance.bind(this), this.delay);
      }

      stop() {
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
      }

      pause() {
        this.isPaused = true;
        this.stop();
      }

      resume() {
        this.isPaused = false;
        this.start();
      }

      advance() {
        const outgoing = this.messages[this.currentIndex];
        this.currentIndex = (this.currentIndex + 1) % this.messages.length;
        const incoming = this.messages[this.currentIndex];

        outgoing.classList.remove('is-active');
        outgoing.classList.add('is-leaving');
        incoming.classList.add('is-active');

        // The departed message now sits above the bar at -100%. It has to return to
        // the waiting position below (+100%) without animating, or it slides back
        // down through the visible text on its way.
        if (this.resetTimer) clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => {
          outgoing.classList.add('promo-bar__message--reset');
          outgoing.classList.remove('is-leaving');
          // Force a reflow so the class above takes effect before it is removed.
          void outgoing.offsetHeight;
          outgoing.classList.remove('promo-bar__message--reset');
        }, this.duration);
      }
    }
  );
}
